import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { MeResponse } from '../lib/types';

// Best-effort location detection. Priority:
//   1. User override saved in localStorage (always wins).
//   2. Cloudflare CF-IPCountry from /api/me — this is the reliable one.
//   3. Timezone heuristic (fallback for local dev or non-CF deployments).
//   4. Hard-coded fallback (Gaborone).
//
// Many devices in southern Africa report Africa/Johannesburg as the timezone
// even when physically in Botswana/Namibia — they share CAT (UTC+2). That's
// why we can't trust timezone alone; the CF header is authoritative.

const COUNTRY_TO_CITY: Record<string, string> = {
  BW: 'Gaborone',
  ZA: 'Johannesburg',
  IN: 'Mumbai',
  NA: 'Windhoek',
  ZM: 'Lusaka',
  ZW: 'Harare',
  GB: 'London',
  US: 'New York',
};

const TZ_TO_COUNTRY: Record<string, string> = {
  'Africa/Gaborone':     'BW',
  'Africa/Johannesburg': 'ZA',
  'Africa/Windhoek':     'NA',
  'Africa/Lusaka':       'ZM',
  'Africa/Harare':       'ZW',
  'Asia/Kolkata':        'IN',
  'Asia/Calcutta':       'IN',
  'Europe/London':       'GB',
  'America/New_York':    'US',
};

const STORAGE_KEY = 'onedesk.location';

interface StoredLocation {
  country: string;
  city: string;
}

const readStored = (): StoredLocation | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.country === 'string' && typeof parsed?.city === 'string') return parsed;
    return null;
  } catch {
    return null;
  }
};

export interface Location {
  country: string;
  city: string;
  source: 'override' | 'cloudflare' | 'timezone' | 'fallback';
}

const FALLBACK: Location = { country: 'BW', city: 'Gaborone', source: 'fallback' };

const fromTimezone = (): Location | null => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = TZ_TO_COUNTRY[tz];
    if (country) {
      return { country, city: COUNTRY_TO_CITY[country] ?? country, source: 'timezone' };
    }
  } catch {
    // ignore
  }
  return null;
};

export const useLocation = (): Location => {
  // Server-supplied country (Cloudflare) — authoritative when present.
  const me = useApi<MeResponse>('/api/me');
  const cfCountry = me.data?.location?.country?.toUpperCase() ?? '';

  const computeInitial = (): Location => {
    const stored = readStored();
    if (stored) return { ...stored, source: 'override' };
    return fromTimezone() ?? FALLBACK;
  };

  const [loc, setLoc] = useState<Location>(computeInitial);

  // Apply server-side country when /api/me arrives. Only overrides if there's
  // no user-saved override and the server value is meaningful.
  useEffect(() => {
    if (!cfCountry) return;
    const stored = readStored();
    if (stored) return; // user override wins
    setLoc({
      country: cfCountry,
      city: COUNTRY_TO_CITY[cfCountry] ?? cfCountry,
      source: 'cloudflare',
    });
  }, [cfCountry]);

  // React to manual override changes (set elsewhere, e.g. a settings panel).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const stored = readStored();
      if (stored) setLoc({ ...stored, source: 'override' });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return loc;
};

// Helper for a future settings panel — sets the override and triggers
// in-tab updates by dispatching a synthetic storage event.
export const setLocationOverride = (country: string, city: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ country, city }));
    // Same-tab event so any active <HeroBand> re-reads.
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  } catch {
    // ignore — localStorage may be disabled
  }
};

export const clearLocationOverride = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  } catch {
    // ignore
  }
};
